/** @module csi/heading/FileBrowseController */
define([
	"axios",
	"dojo/_base/declare",
	"dojo/Deferred",
	"src/c8-platform/core/request",
	"underscore",
	"csi/action/_3DFileValidationMixin",
	"csi/comm/Aspera",
	"csi/comm/PostRequest",
	"csi/heading/AttributeController",
	"csi/html",
	"csi/locale",
	"csi/service/fileService",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/widget/MessageDialog",
	"csi/xml"
], function (axios, declare, Deferred, {postFormData, REQUEST_HANDLER, getFormDataConfig}, _, ThreeDFileValidationMixin, Aspera, PostRequest, AttributeController, html, locale, fileService, sessionConstants, {browserStore}, MessageDialog, xml) {

	return declare([AttributeController, ThreeDFileValidationMixin], {
		/** @property {boolean} bInterrupt - set to true so that controller is stopped on next action when user cancels file browser */
		bInterrupt: true,

		/** @property {File} fileToUpload - file selected for upload */
		fileToUpload: null,

		/** @property {boolean} canceledFileSelect - true when user cancels Aspera file select */
		canceledFileSelect: false,

		/**
		 * @property {Deferred} _inputDeferred - deferred object for browsing for file; we cannot trap canceling the file browser,
		 * so instead reject this deferred when action is interrupted
		 */
		_inputDeferred: null,
				
		/**
		 * @override
		 * Return deferred that will be resolved when a file is selected for upload
		 */
		_preAction: function () {
			var self = this;
			// Aspera has its own file select dialog, we must use it instead for Aspera
			if (Aspera.isEnabled()) {
				return new Deferred().resolve();
			}
			var dfd = this._inputDeferred = html.selectFile();

			return dfd.then(
				function (files) {
					self.fileToUpload = files[0];
				}
			);
		},

		/**
		 * @override
		 * If another action started, then user must have cancelled file browser; so, reject the input's deferred object which will stop the _preAction function
		 * and end the controller
		 */
		interruptProcess: function () {
			this._inputDeferred && this._inputDeferred.reject();
		},

		/**
		 * @override
		 * Validate selected file
		 */
		preProcess: function () {
			// summary: start file upload
			var self = this;
			var parentNode = this.getParent();
			const node = this.getNode();
			var is3D = parentNode && parentNode.Is3D;
			if (Aspera.isEnabled()) {
				this.disableActionView(true);
				// For file replace operation we submit CurrentRevision CNL
				var options = is3D ? {
					allowedFileTypes: [{
						filter_name: locale.resource("3DZip"),
						extensions: ["zip", "zfab", "trm", "btn", "bth", "zpac", "sst", "zsp", "zpl", "zsd"]
					}]
				} : {};
				const docContainerUrl = node.$Type == "DocumentRevision" ? node.CurrentRevision : node.$URL;
				const fileAttr = this.heading.m_id;
				this.canceledFileSelect = false;
				Aspera.uploadFile(docContainerUrl, fileAttr, true, true, this, options).then(
					(response) => {
						self._onPublishResponse(response);
					},
					(msg) => {
						// Do not show error if user cancels file selection, but show error if Aspera license is expired
						self.m_error = msg || "";
						if (!Aspera.isValid()) {
							self.canceledFileSelect = true;
						}
						self.postProcess(false);
					}
				);
				return;
			}

			var file = this.fileToUpload;
			this.createAbortController(this.m_urlNode, this.heading.m_id, file.size);		

			function uploadFail(msg) {
				var message = msg || file.name + "\n" + self.getError("_ErrorFile");
				self.displayErrorDialog({ message: message });
			}

			function uploadSuccess() {
				self.process();
			}

			// check extensions and if files with image extensions are really images or not
			this.when(fileService.validateFile(file), uploadSuccess, uploadFail);
		
		},

		/**
		 * @override
		 * Call server to publish new file to attribute, and store filename in "*.File" attribute
		 */
		async onEnd() {
			const file = this.fileToUpload;
			const postRequest = new PostRequest("SiteAdmin", "MultiFilePublish")
				.addParameter("Name", file.name)
				.addParameter("NewFileAttrId", this.heading.m_id)
				.addParameter("URL", this.m_urlNode)
				.addParameter("SessionURL", sessionConstants.sessionUrl)
				.addParameter("IdStatus", this.ren.getIdStatus(this.heading))
				.addFiles(file, null, "FormFileFieldName");

			const formData = postRequest._createFormData(postRequest.content);
			
			let abortController = browserStore.getFileUploadController(this.m_urlNode, this.heading.m_id);
			
			if (!abortController) {
				abortController = this.createAbortController(this.m_urlNode, this.heading.m_id, file.size);
			}
			
			const signal = abortController.signal;
			
			try {
				await postFormData(formData, getFormDataConfig(null, signal));
			} catch(err) {

				// AbortError may be thrown when aborting an upload. This is expected and the error should not be shown to the user
				if (err.name === "AbortError") {
					return;
				}

				this.displayErrorDialog({
					message: err.message,
					onResponse: () => {}
				});
			};
					
		},
		
		/**
		 * Creates an AbortController and adds it to the file upload map
		 * @param {Node} urlNode
		 * @param {string} headingId
		 * @param {number} fileSize
		 * @return {AbortController} 
		 */
		createAbortController: function(urlNode, headingId, fileSize) {
			const abortController = new AbortController();			
			browserStore.addToFileUploadMap(urlNode, headingId, fileSize, abortController);
			return abortController;
		},

		/**
		 * @override
		 * customize the error message, can't be able to tell user what exact reason is, do this for now
		 */
		getError: function (suffix, params) {
			if (Aspera.isValid()) {
				return this.m_error;
			}
			return this.canceledFileSelect ? null : locale.resource("FileBrowse_Error");
		},		

		/**
		 * @override
		 * Store new attribute value
		 */
		onPublishResponse: function (response) {
			this.newValue = this.getNode()[this.heading.m_id];
		},

		/**
		 * @override
		 * Add File.Status attribute while upload is occuring so progress bar appears
		 */
		toWriter() {
			this.m_writer.changeNode(this.m_urlNode).addAttribute(this.ren.getIdStatus(this.heading), xml.ATTR_STRING, "");

			this.inherited(arguments);
		}

	});
});